import React, { FC } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RCardGet } from '../../../../../../apiRPC/card';
import { useAdditionalFieldsState } from './hooks/useAdditionalFieldsState';
import { useAdditionalFieldsPersistence } from './hooks/useAdditionalFieldsPersistence';
import { useGridApiCampaignField } from './hooks/useGridApiCampaignField';
import { useAdditionalFieldsHandlers } from './hooks/useAdditionalFieldsHandlers';
import { useAccordionDynamicObject } from './hooks/useAccordionDynamicObject';
import { CampaignSection } from './components/sections/CampaignSection';
import { AgencyClientSection } from './components/sections/AgencyClientSection';
import { PeriodBillingSection } from './components/sections/PeriodBillingSection';
import { FinancialFieldsSection } from './components/sections/FinancialFieldsSection';
import { MonthlyBreakdownSection } from './components/sections/MonthlyBreakdownSection';
import { StatsUrlSection } from './components/sections/StatsUrlSection';
import { ProductCategorySection } from './components/sections/ProductCategorySection';
import { CampaignMetaSection } from './components/sections/CampaignMetaSection';
import { ParticipantsSection } from './components/sections/ParticipantsSection';

type Props = {
  card: RCardGet;
};

export const AdditionalFieldsAccordion: FC<Props> = ({ card }) => {
  const state = useAdditionalFieldsState(card);
  const persistence = useAdditionalFieldsPersistence({
    card,
    values: state.values,
    setValues: state.setValues,
    mainDynamicObjectId: state.mainDynamicObjectId,
    canPersistDerived: state.canPersistDerived,
    initialValues: state.initialValues,
    initialBindings: state.initialBindings,
    parsedMainDynamicObjectId: state.parsedMainDynamicObjectId,
    parsedLegacyDynamicObjectIds: state.parsedLegacyDynamicObjectIds,
  });
  const gridApiCampaignField = useGridApiCampaignField(card);

  const handlers = useAdditionalFieldsHandlers({
    values: state.values,
    setValues: state.setValues,
    setPeriodErrors: state.setPeriodErrors,
    saveField: persistence.saveField,
    debouncedSaveField: persistence.debouncedSaveField,
  });

  const { handleAccordionChange } = useAccordionDynamicObject({
    card,
    values: state.values,
    mainDynamicObjectId: state.mainDynamicObjectId,
    isDynamicObjectCreating: state.isDynamicObjectCreating,
    setIsAdditionalFieldsExpanded: state.setIsAdditionalFieldsExpanded,
    setIsDynamicObjectCreating: state.setIsDynamicObjectCreating,
    setMainDynamicObjectId: state.setMainDynamicObjectId,
  });

  return (
    <Accordion
      expanded={state.isAdditionalFieldsExpanded}
      onChange={handleAccordionChange}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>Дополнительные поля</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2} p={2}>
          <CampaignSection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            gridApiCampaign={gridApiCampaignField.gridApiCampaign}
            gridApiCampaignError={gridApiCampaignField.gridApiCampaignError}
            isGridApiCampaignDisabled={
              gridApiCampaignField.isGridApiCampaignDisabled
            }
            gridApiCampaignHelperText={
              gridApiCampaignField.gridApiCampaignHelperText
            }
            handleGridApiCampaignChange={
              gridApiCampaignField.handleGridApiCampaignChange
            }
            handleGridApiCampaignBlur={
              gridApiCampaignField.handleGridApiCampaignBlur
            }
            handleGridApiCampaignKeyDown={
              gridApiCampaignField.handleGridApiCampaignKeyDown
            }
            handleTextChange={handlers.handleTextChange}
            handleBlur={handlers.handleBlur}
            handleTextKeyDown={handlers.handleTextKeyDown}
          />

          <AgencyClientSection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            setValues={state.setValues}
            debouncedSaveField={persistence.debouncedSaveField}
            saveField={persistence.saveField}
            clearSaveFieldTimeout={persistence.clearSaveFieldTimeout}
            setDictionaryFieldValidity={persistence.setDictionaryFieldValidity}
          />

          <Box display="flex" gap={4} flexWrap="wrap" alignItems="flex-start">
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ flex: 1, minWidth: 300 }}
            >
              <PeriodBillingSection
                values={state.values}
                isFieldsDisabled={state.isFieldsDisabled}
                periodErrors={state.periodErrors}
                handleDateChange={handlers.handleDateChange}
                handleDateClear={handlers.handleDateClear}
                handleSelectChange={handlers.handleSelectChange}
                handleSelectClear={handlers.handleSelectClear}
              />
              <FinancialFieldsSection
                values={state.values}
                isFieldsDisabled={state.isFieldsDisabled}
                setValues={state.setValues}
                debouncedSaveField={persistence.debouncedSaveField}
              />
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ flex: 1, minWidth: 300 }}
            >
              <MonthlyBreakdownSection
                values={state.values}
                isFieldsDisabled={state.isFieldsDisabled}
                setValues={state.setValues}
                debouncedSaveField={persistence.debouncedSaveField}
                handleBooleanChange={handlers.handleBooleanChange}
                handleTextChange={handlers.handleTextChange}
                handleBlur={handlers.handleBlur}
                handleTextKeyDown={handlers.handleTextKeyDown}
              />
            </Box>
          </Box>

          <StatsUrlSection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            handleTextChange={handlers.handleTextChange}
            handleBlur={handlers.handleBlur}
            handleTextKeyDown={handlers.handleTextKeyDown}
          />

          <ProductCategorySection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            handleSelectChange={handlers.handleSelectChange}
            handleSelectClear={handlers.handleSelectClear}
            handleBooleanChange={handlers.handleBooleanChange}
          />

          <CampaignMetaSection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            handleTextChange={handlers.handleTextChange}
            handleBlur={handlers.handleBlur}
            handleTextKeyDown={handlers.handleTextKeyDown}
            handleSelectChange={handlers.handleSelectChange}
            handleSelectClear={handlers.handleSelectClear}
          />

          <ParticipantsSection
            values={state.values}
            isFieldsDisabled={state.isFieldsDisabled}
            handleMultiselectChange={handlers.handleMultiselectChange}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
